import { Notification } from '../notifications/Notification.js';
import { NotificationEvent } from '../notifications/NotificationEvent.js';
import { NotificationSubscriberType } from '../notifications/NotificationSubscription.js';
import { NOTIFICATION_TEMPLATES_BY_EVENT, interpolateTemplate } from '../notifications/NotificationTemplate.js';
import { generateADFEmailBody } from './adf.utils.js';

// Inbound channel metadata — categorizes how an inbound lead arrived
const InboundChannel = {
    EkhoWidget: 'EKHO_WIDGET',
    FormHijack: 'FORM_HIJACK',
    MarketplaceVdp: 'MARKETPLACE_VDP',
    DealerWebsite: 'DEALER_WEBSITE',
    Sms: 'SMS',
    Other: 'OTHER',
};
// Supporting enums
var LeadType;
(function (LeadType) {
    LeadType["ABANDONED_CART"] = "ABANDONED_CART";
    LeadType["REJECTED_FINANCING"] = "REJECTED_FINANCING";
    LeadType["BUY_IN_PERSON"] = "BUY_IN_PERSON";
    LeadType["ABANDONED_PREORDER_CART"] = "ABANDONED_PREORDER_CART";
    LeadType["INQUIRY"] = "INQUIRY";
    LeadType["INBOUND"] = "INBOUND";
})(LeadType || (LeadType = {}));
var LeadPipelineType;
(function (LeadPipelineType) {
    LeadPipelineType["DIGITAL_RETAIL_PIPELINE"] = "DIGITAL_RETAIL_PIPELINE";
    LeadPipelineType["INBOUND_PIPELINE"] = "INBOUND_PIPELINE";
})(LeadPipelineType || (LeadPipelineType = {}));
var LeadPipelineName;
(function (LeadPipelineName) {
    LeadPipelineName["DIGITAL_RETAIL_PIPELINE"] = "Digital retail pipeline";
    LeadPipelineName["INBOUND_PIPELINE"] = "Inbound pipeline";
})(LeadPipelineName || (LeadPipelineName = {}));
const LeadStageType = {
    // Common stages
    NEW: 'NEW',
    CONTACTED: 'CONTACTED',
    ENGAGED: 'ENGAGED',
    ON_HOLD: 'ON_HOLD',
    CLOSED_WON: 'CLOSED_WON',
    CLOSED_LOST: 'CLOSED_LOST',
    // Inbound-specific stages
    NEEDS_ATTENTION: 'NEEDS_ATTENTION',
    BOOKED: 'BOOKED',
    TO_REACH_OUT: 'TO_REACH_OUT',
};
var LeadStageName;
(function (LeadStageName) {
    LeadStageName["NEW"] = "New";
    LeadStageName["CONTACTED"] = "Contacted";
    LeadStageName["ENGAGED"] = "Engaged";
    LeadStageName["ON_HOLD"] = "On hold";
    LeadStageName["CLOSED_WON"] = "Closed won";
    LeadStageName["CLOSED_LOST"] = "Closed lost";
    LeadStageName["TO_REACH_OUT"] = "To reach out";
    LeadStageName["NEEDS_ATTENTION"] = "Needs attention";
    LeadStageName["BOOKED"] = "Booked";
})(LeadStageName || (LeadStageName = {}));
const OriginChannel = {
    DEALER_CHECKOUT: 'DEALER_CHECKOUT',
    OEM_CHECKOUT: 'OEM_CHECKOUT',
    MARKETPLACE_VDP: 'MARKETPLACE_VDP',
    SHOWROOM: 'SHOWROOM',
    PHONE: 'PHONE',
    OTHER: 'OTHER',
    WEBSITE_FORM: 'WEBSITE_FORM',
};
const PreQualificationStatus = {
    PRE_QUALIFIED: 'PRE_QUALIFIED',
    REJECTED: 'REJECTED',
};
const LeadSourceType = {
    CHECKOUT_CART: 'CHECKOUT_CART',
    PREORDER: 'PREORDER',
    PURCHASED_VEHICLE: 'PURCHASED_VEHICLE',
    INQUIRY: 'INQUIRY',
    INBOUND: 'INBOUND',
};
const StageChangeType = {
    EKHO: 'EKHO',
    USER: 'USER',
};
// Pipeline mappings
const LEAD_TYPE_TO_PIPELINE = {
    [LeadType.ABANDONED_CART]: LeadPipelineType.DIGITAL_RETAIL_PIPELINE,
    [LeadType.REJECTED_FINANCING]: LeadPipelineType.DIGITAL_RETAIL_PIPELINE,
    [LeadType.ABANDONED_PREORDER_CART]: LeadPipelineType.DIGITAL_RETAIL_PIPELINE,
    [LeadType.BUY_IN_PERSON]: LeadPipelineType.INBOUND_PIPELINE,
    [LeadType.INQUIRY]: LeadPipelineType.INBOUND_PIPELINE,
    [LeadType.INBOUND]: LeadPipelineType.INBOUND_PIPELINE,
};
// Initial stage mapping
const LEAD_TYPE_TO_INITIAL_STAGE = {
    [LeadType.ABANDONED_CART]: LeadStageType.TO_REACH_OUT,
    [LeadType.REJECTED_FINANCING]: LeadStageType.TO_REACH_OUT,
    [LeadType.BUY_IN_PERSON]: LeadStageType.TO_REACH_OUT,
    [LeadType.ABANDONED_PREORDER_CART]: LeadStageType.TO_REACH_OUT,
    [LeadType.INQUIRY]: LeadStageType.TO_REACH_OUT,
    [LeadType.INBOUND]: LeadStageType.TO_REACH_OUT,
};
// Pipeline configurations
const LEAD_PIPELINES = {
    [LeadPipelineType.DIGITAL_RETAIL_PIPELINE]: {
        type: LeadPipelineType.DIGITAL_RETAIL_PIPELINE,
        name: LeadPipelineName.DIGITAL_RETAIL_PIPELINE,
        description: 'Pipeline for online order leads',
        stages: [
            {
                stage: LeadStageType.ON_HOLD,
                name: LeadStageName.ON_HOLD,
                description: 'Lead is temporarily paused',
                allowedTransitions: [LeadStageType.TO_REACH_OUT, LeadStageType.CLOSED_LOST],
            },
            {
                stage: LeadStageType.TO_REACH_OUT,
                name: LeadStageName.TO_REACH_OUT,
                description: 'Lead needs initial contact',
                isInitialStage: true,
                allowedTransitions: [
                    LeadStageType.CONTACTED,
                    LeadStageType.ON_HOLD,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.CONTACTED,
                name: LeadStageName.CONTACTED,
                description: 'Initial contact made',
                allowedTransitions: [
                    LeadStageType.ENGAGED,
                    LeadStageType.ON_HOLD,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.ENGAGED,
                name: LeadStageName.ENGAGED,
                description: 'Customer is actively engaged',
                allowedTransitions: [LeadStageType.CLOSED_WON, LeadStageType.CLOSED_LOST],
            },
            {
                stage: LeadStageType.CLOSED_WON,
                name: LeadStageName.CLOSED_WON,
                description: 'Customer completed purchase',
                isFinalStage: true,
                allowedTransitions: [],
            },
            {
                stage: LeadStageType.CLOSED_LOST,
                name: LeadStageName.CLOSED_LOST,
                description: 'Lead did not convert',
                isFinalStage: true,
                allowedTransitions: [],
            },
        ],
        leadTypes: [
            LeadType.ABANDONED_CART,
            LeadType.REJECTED_FINANCING,
            LeadType.ABANDONED_PREORDER_CART,
        ],
        isActive: true,
        version: 1,
    },
    [LeadPipelineType.INBOUND_PIPELINE]: {
        type: LeadPipelineType.INBOUND_PIPELINE,
        name: 'Inbound Pipeline',
        description: 'Pipeline for inbound leads (website forms, widgets, AI assistants)',
        stages: [
            {
                stage: LeadStageType.ON_HOLD,
                name: LeadStageName.ON_HOLD,
                description: 'Lead is temporarily paused',
                allowedTransitions: [
                    LeadStageType.TO_REACH_OUT,
                    LeadStageType.NEEDS_ATTENTION,
                    LeadStageType.ENGAGED,
                    LeadStageType.BOOKED,
                    LeadStageType.CLOSED_WON,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.TO_REACH_OUT,
                name: LeadStageName.TO_REACH_OUT,
                description: 'Lead needs initial contact',
                isInitialStage: true,
                allowedTransitions: [
                    LeadStageType.NEEDS_ATTENTION,
                    LeadStageType.ON_HOLD,
                    LeadStageType.ENGAGED,
                    LeadStageType.BOOKED,
                    LeadStageType.CLOSED_WON,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.NEEDS_ATTENTION,
                name: LeadStageName.NEEDS_ATTENTION,
                description: 'Conversation with lead was escalated',
                allowedTransitions: [
                    LeadStageType.ON_HOLD,
                    LeadStageType.ENGAGED,
                    LeadStageType.BOOKED,
                    LeadStageType.CLOSED_WON,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.ENGAGED,
                name: LeadStageName.ENGAGED,
                description: 'Active engagement with lead',
                allowedTransitions: [
                    LeadStageType.BOOKED,
                    LeadStageType.CLOSED_WON,
                    LeadStageType.CLOSED_LOST,
                ],
            },
            {
                stage: LeadStageType.BOOKED,
                name: LeadStageName.BOOKED,
                description: 'Lead has a confirmed appointment or commitment',
                allowedTransitions: [LeadStageType.CLOSED_WON, LeadStageType.CLOSED_LOST],
            },
            {
                stage: LeadStageType.CLOSED_WON,
                name: LeadStageName.CLOSED_WON,
                description: 'Lead successfully converted',
                isFinalStage: true,
                allowedTransitions: [],
            },
            {
                stage: LeadStageType.CLOSED_LOST,
                name: LeadStageName.CLOSED_LOST,
                description: 'Lead did not convert',
                isFinalStage: true,
                allowedTransitions: [],
            },
        ],
        leadTypes: [LeadType.BUY_IN_PERSON, LeadType.INBOUND],
        isActive: true,
        version: 1,
    },
};
/**
 * Lead class that implements Notifiable
 *
 * Represents a lead in the Lead Management system and can generate
 * notifications when leads are created. Generates ADF-formatted XML for CRM integration.
 */
class Lead {
    constructor(leadData, enrichmentData = {}) {
        this.leadData = leadData;
        this.enrichmentData = enrichmentData;
        this.id = leadData.id;
        this.domainsServicesProviderId = leadData.programOwnerId;
        this.programOwnerId = leadData.programOwnerId;
        this.subscriberIdsBySubscriberType = {
            [NotificationSubscriberType.BUYER]: [],
            [NotificationSubscriberType.OEM]: [],
            [NotificationSubscriberType.SERVICES_PROVIDER]: leadData.svpId ? [leadData.svpId] : [],
        };
        this.metadata = { buyerId: leadData.buyerId };
    }
    /**
     * Convert the Lead to a notification event.
     */
    toNotificationEvent() {
        return NotificationEvent.LEAD_CREATED;
    }
    /**
     * Convert the Lead to a notification for a specific subscriber type.
     */
    toNotification(subscriberType, _baseActionUrl) {
        if (subscriberType !== NotificationSubscriberType.SERVICES_PROVIDER) {
            return null;
        }
        const event = this.toNotificationEvent();
        const template = event && NOTIFICATION_TEMPLATES_BY_EVENT[event]?.[subscriberType];
        if (!template)
            return null;
        // Check if at least one channel is configured
        if (template.email === null &&
            template.sms === null &&
            template.webhook === null &&
            template.slack === null) {
            return null;
        }
        const adfEmailBody = generateADFEmailBody(this.toADFLeadData());
        const subscriberId = this.subscriberIdsBySubscriberType[subscriberType][0] || '';
        // Format leadType for display (e.g., "ABANDONED_CART" → "Abandoned Cart")
        const formattedLeadType = this.leadData.type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        // Build email payload (use empty values if email is not configured)
        const email = template.email
            ? {
                subject: interpolateTemplate(template.email.subject, {
                    buyerFullName: this.leadData.fullName ?? 'Unknown Customer',
                    vendorName: this.enrichmentData.vendor?.name,
                    leadType: formattedLeadType,
                }),
                body: adfEmailBody,
            }
            : { subject: '', body: '' };
        // Build SMS payload (use empty string if SMS is not configured)
        const sms = template.sms ?? '';
        // Build webhook payload (use empty object if webhook is not configured)
        const webhook = template.webhook ?? {};
        return new Notification('', Date.now(), Date.now(), this.id, event, email, sms, webhook, template.schedule, subscriberType, subscriberId, undefined, 0, false, this.metadata);
    }
    toADFLeadData() {
        return {
            dateCreated: this.leadData.dateCreated,
            leadId: this.leadData.id,
            leadType: this.leadData.type,
            customer: {
                fullName: this.leadData.fullName ?? 'Unknown Customer',
                email: this.leadData.email,
                phone: this.leadData.phoneNumber,
                address: this.enrichmentData.customerAddress,
            },
            vehicles: this.enrichmentData.vehicles,
            vendor: {
                name: this.enrichmentData.vendor?.name ?? 'N/A',
                email: this.enrichmentData.vendor?.email,
                phone: this.enrichmentData.vendor?.phone,
            },
            comments: this.enrichmentData.comments ?? {},
        };
    }
}

export { InboundChannel, LEAD_PIPELINES, LEAD_TYPE_TO_INITIAL_STAGE, LEAD_TYPE_TO_PIPELINE, Lead, LeadPipelineName, LeadPipelineType, LeadSourceType, LeadStageName, LeadStageType, LeadType, OriginChannel, PreQualificationStatus, StageChangeType };
//# sourceMappingURL=Lead.js.map
